import { useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { constants } from "helpers"
import useChainProviderHook from "@/hooks/useChainProvider"
import type {
  SxSummaryFixture,
  SxSummaryLeague,
  SxSummarySport,
} from "../types/markets"
import { SX_CONFIG } from "../constants"
import { useSxSportsNavigation } from "./useSxSportsNavigation"
import {
  transformSxActiveMarkets,
  transformSxMarketsForComponents,
} from "../utils/transformSxMarkets"
import { isAfter48Hours } from "@/utils/helper"

// Response interface for league summary endpoint
interface SxLeagueSummaryResponse {
  status: string
  data: {
    scope: "league"
    sportId: number
    leagueId: number
    label: string
    eventsByType: Record<string, number>
    days: Array<{
      day: string
      marketTypes: string[]
      fixtures: SxSummaryFixture[]
    }>
  }
}

// Result interface for the hook
interface UseSxLeagueMarketsResult {
  sports: SxSummarySport[]
  league: SxSummaryLeague | null
  fixtures: SxSummaryFixture[]
  markets: any[]
  marketsCount: number
  isLoading: boolean
  isFetching: boolean // For live switch transitions
  error: Error | null
}

/**
 * Helper function to fetch league summary
 */
const fetchLeagueSummary = async (
  chainId: number,
  leagueId: number,
  betGroup: string = "1X2"
): Promise<SxLeagueSummaryResponse> => {
  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const url = `${constants.getSxBetApiUrl()}/summary/league/${TOKEN}/${leagueId}/${betGroup}?chainId=${chainId}`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const result: SxLeagueSummaryResponse = await response.json()
  if (result.status !== "success") {
    throw new Error(`API error: ${result.status}`)
  }

  return result
}

/**
 * Hook for fetching league markets
 */
export const useSxLeagueMarkets = (
  leagueId: number,
  enabled: boolean = true
): UseSxLeagueMarketsResult => {
  const { chainId, isSXChain } = useChainProviderHook()
  const { data: navData } = useSxSportsNavigation()
  const { leagues } = navData || {}

  // Get available bet groups for this league from navigation data
  const betGroups = useMemo(() => {
    if (!leagues) {
      return ["game-lines"]
    }
    const league = leagues.find(l => l.leagueId === leagueId)
    return league?.eventsByType
      ? Object.keys(league.eventsByType)
      : ["game-lines"]
  }, [leagues, leagueId])

  // Fetch data for each bet group in parallel
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["sx-league-summary", chainId, leagueId, betGroups],
    queryFn: async () => {
      const responses = await Promise.all(
        betGroups.map(betGroup =>
          fetchLeagueSummary(chainId, leagueId, betGroup)
        )
      )

      // Merge all responses
      const mergedData = responses.reduce((acc, response) => {
        if (!acc.data) {
          return response
        }

        // Merge days and their fixtures
        response.data.days.forEach(day => {
          const existingDay = acc.data.days.find(d => d.day === day.day)
          if (existingDay) {
            // Merge market types
            existingDay.marketTypes = [
              ...new Set([...existingDay.marketTypes, ...day.marketTypes]),
            ]
            // Merge fixtures
            day.fixtures.forEach(fixture => {
              const existingFixture = existingDay.fixtures.find(
                f => f.id === fixture.id
              )
              if (existingFixture) {
                existingFixture.markets = [
                  ...existingFixture.markets,
                  ...fixture.markets,
                ]
                existingFixture.marketHashes = [
                  ...existingFixture.marketHashes,
                  ...fixture.marketHashes,
                ]
                existingFixture.betGroups = [
                  ...new Set([
                    ...existingFixture.betGroups,
                    ...fixture.betGroups,
                  ]),
                ]
              } else {
                existingDay.fixtures.push(fixture)
              }
            })
          } else {
            acc.data.days.push(day)
          }
        })

        // Merge eventsByType
        acc.data.eventsByType = {
          ...acc.data.eventsByType,
          ...response.data.eventsByType,
        }

        return acc
      })

      return mergedData
    },
    enabled: enabled && !!chainId && betGroups.length > 0,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 30, // 30 seconds
    staleTime: 1000 * 30, // 30 seconds
    refetchOnMount: true, // Always fresh data
    refetchOnReconnect: true,
  })

  // Process the data to extract league information and transform markets
  const processedData = useMemo(() => {
    if (!data?.data || !data.data.days || data.data.days.length === 0) {
      return {
        sports: [],
        league: null,
        fixtures: [],
        markets: [],
        marketsCount: 0,
      }
    }

    const { data: responseData } = data

    // Collect all fixtures from all days
    const allFixtures: SxSummaryFixture[] = []
    const allMarketTypes: string[] = []

    responseData.days.forEach(day => {
      if (day.fixtures && day.fixtures.length > 0) {
        const gameTime = new Date(day.fixtures[0].gameTime).getTime()
        const now = Date.now()
        const isLocked = isAfter48Hours(day.fixtures[0].gameTime)
        if (gameTime > now && !isLocked) {
          allFixtures.push(...day.fixtures)
        }
      }
      if (day.marketTypes && day.marketTypes.length > 0) {
        const gameTime = new Date(day.fixtures[0].gameTime).getTime()
        const now = Date.now()
        if (gameTime > now) {
          allMarketTypes.push(...day.marketTypes)
        }
      }
    })

    const league: SxSummaryLeague = {
      leagueId: responseData.leagueId,
      leagueLabel: responseData.label,
      marketTypes: [...new Set(allMarketTypes)], // Remove duplicates
      fixtures: allFixtures,
    }

    // Create a sport structure for transformation
    const sportData: SxSummarySport = {
      sportId: responseData.sportId,
      leagues: [league],
    }
    // Transform the data using the same transformer as other hooks
    const transformedSports = transformSxActiveMarkets([sportData], chainId)

    // Transform markets for SXOutcomeButton compatibility
    const transformedMarkets = transformSxMarketsForComponents(
      [sportData],
      chainId,
      isSXChain
    )

    // Count only fixtures with valid markets
    const validFixturesCount = league.fixtures.filter(fixture => {
      return (
        fixture.markets &&
        fixture.markets.some(
          market => market.bestOddsOutcomeOne && market.bestOddsOutcomeTwo
        )
      )
    }).length
    const marketsCount = validFixturesCount

    return {
      sports: transformedSports,
      league,
      fixtures: allFixtures,
      markets: transformedMarkets,
      marketsCount,
    }
  }, [data])

  return {
    sports: processedData.sports,
    league: processedData.league,
    fixtures: processedData.fixtures,
    markets: processedData.markets,
    marketsCount: processedData.marketsCount,
    isLoading: isLoading,
    isFetching,
    error: error as Error | null,
  }
}

/**
 * Legacy hook name for backward compatibility
 * @deprecated Use useSxLeagueMarkets instead
 */
export const useSxStreamingLeagueMarket = useSxLeagueMarkets
